/*
 This file is part of GNU Taler
 (C) 2021-2024 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import {
  AccessToken,
  CurrencySpecification,
  getMerchantAccountKycStatusSimplified,
  MerchantAccountKycStatus,
  MerchantAccountKycStatusSimplified,
  Paytos,
  TalerError,
  TalerHttpError,
  TalerMerchantManagementResultByMethod,
} from "@gnu-taler/taler-util";
import { LONG_POLL_DELAY, useLongPolling } from "@gnu-taler/web-util/browser";
import { useSessionContext } from "../context/session.js";

// Fix default import https://github.com/microsoft/TypeScript/issues/49189
import _useSWR, { mutate, SWRHook } from "swr";
import { useEffect } from "preact/hooks";
import { useState } from "preact/hooks";
const useSWR = _useSWR as unknown as SWRHook;

export function revalidateInstanceDetails() {
  return mutate(
    (key) =>
      Array.isArray(key) && key[key.length - 1] === "getCurrentInstanceDetails",
    undefined,
    { revalidate: true },
  );
}

export function useInstanceDetails() {
  const { state, lib } = useSessionContext();

  async function fetcher([token]: [AccessToken]) {
    return await lib.instance.getCurrentInstanceDetails(token);
  }

  const { data, error } = useSWR<
    TalerMerchantManagementResultByMethod<"getCurrentInstanceDetails">,
    TalerHttpError
  >([state.token, "getCurrentInstanceDetails"], fetcher);

  if (data) return data;
  if (error) return error;
  return undefined;
}

export function revalidateInstanceKYCDetails() {
  return mutate(
    (key) =>
      Array.isArray(key) &&
      key[key.length - 1] === "getCurrentInstanceKycStatus",
    undefined,
    { revalidate: true },
  );
}

export function useInstanceKYCDetailsLongPolling() {
  const { state, lib } = useSessionContext();
  const token = state.token;

  async function fetcher([token]: [AccessToken]) {
    return await lib.instance.getCurrentInstanceKycStatus(token);
  }

  const { data, error, mutate } = useSWR<
    TalerMerchantManagementResultByMethod<"getCurrentInstanceKycStatus">,
    TalerHttpError
  >([token, "getCurrentInstanceKycStatus"], fetcher);

  const result = useLongPolling(
    data,
    (result) => {
      return (
        result !== undefined &&
        result.type === "ok" &&
        result.body.etag !== undefined
      );
    },
    async (ct, latestData) => {
      if (
        latestData === undefined ||
        latestData.type === "fail" ||
        !latestData.body.etag
      )
        return undefined;
      const r = await lib.instance.getCurrentInstanceKycStatus(token!, {
        longpoll: {
          type: "state-change",
          etag: latestData.body.etag,
          timeout: LONG_POLL_DELAY,
        },
        ct,
      });
      await mutate(r, { revalidate: false });
      return r;
    },
    [],
    { minTime: LONG_POLL_DELAY },
  );

  if (error) {
    return error;
  }
  return result;
}

export function useInstanceKYCSimplifiedWorstStatusLongPolling(
  account?: Paytos.URI,
) {
  const kycStatus = useInstanceKYCDetailsLongPolling();

  const allKycData =
    kycStatus !== undefined &&
    !(kycStatus instanceof TalerError) &&
    kycStatus.type === "ok"
      ? kycStatus.body.kyc_data
      : [];

  const uri_str =
    account === undefined ? undefined : Paytos.toFullString(account);

  const filtered =
    uri_str === undefined
      ? allKycData
      : allKycData.filter((data) => data.payto_uri === uri_str);

  const r = filtered.reduce((prev, cur) => {
    const st = getMerchantAccountKycStatusSimplified(cur.status);
    if (st > prev) return st;
    return prev;
  }, MerchantAccountKycStatusSimplified.OK);

  return r;
}

export function useInstanceKYCSimplifiedBestStatusLongPolling() {
  const kycStatus = useInstanceKYCDetailsLongPolling();

  const allKycData =
    kycStatus !== undefined &&
    !(kycStatus instanceof TalerError) &&
    kycStatus.type === "ok"
      ? kycStatus.body.kyc_data
      : [];

  return allKycData.reduce(
    (prev, cur) => {
      const st = getMerchantAccountKycStatusSimplified(cur.status);
      if (prev === undefined) return st;
      if (st < prev) return st;
      return prev;
    },
    undefined as MerchantAccountKycStatusSimplified | undefined,
  );
}

export function useAvailableCurrencies(): string[] {
  const kyc = useInstanceKYCDetails();

  const status =
    kyc instanceof TalerError || !kyc || kyc.type === "fail"
      ? undefined
      : kyc.body;

  const cs = !status ? [] : status.kyc_data.map((st) =>
    st.status === MerchantAccountKycStatus.READY
      ? st.exchange_currency
      : undefined,
  );
  return cs.filter((c): c is string => !!c);
}

export function useInstanceKYCDetails() {
  const { state, lib } = useSessionContext();
  const token = state.token;

  async function fetcher([token]: [AccessToken]) {
    return await lib.instance.getCurrentInstanceKycStatus(token);
  }

  const { data, error } = useSWR<
    TalerMerchantManagementResultByMethod<"getCurrentInstanceKycStatus">,
    TalerHttpError
  >([token, "getCurrentInstanceKycStatus"], fetcher);

  if (error) return error;
  return data;
}

export function revalidateManagedInstanceDetails() {
  return mutate(
    (key) => Array.isArray(key) && key[key.length - 1] === "getInstanceDetails",
    undefined,
    { revalidate: true },
  );
}
export function useManagedInstanceDetails(instanceId: string) {
  const { state, lib } = useSessionContext();

  async function fetcher([token, instanceId]: [AccessToken, string]) {
    return await lib.instance.getInstanceDetails(token, instanceId);
  }

  const { data, error } = useSWR<
    TalerMerchantManagementResultByMethod<"getInstanceDetails">,
    TalerHttpError
  >([state.token, instanceId, "getInstanceDetails"], fetcher);

  if (data) return data;
  if (error) return error;
  return undefined;
}

export function revalidateBackendInstances() {
  return mutate(
    (key) => Array.isArray(key) && key[key.length - 1] === "listInstances",
    undefined,
    { revalidate: true },
  );
}

export function useBackendInstances() {
  const { state, lib } = useSessionContext();

  async function fetcher([token]: [AccessToken]) {
    return await lib.instance.listInstances(token);
  }

  const { data, error } = useSWR<
    TalerMerchantManagementResultByMethod<"listInstances">,
    TalerHttpError
  >([state.token, "listInstances"], fetcher);

  if (data) return data;
  if (error) return error;
  return undefined;
}
